import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  MagnifyingGlassIcon,
  MapIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { buildLocationTree, pathKeyFromSegments, type TreeNode } from "./planModel";
import "./huelleDetail.css";

export interface SubLocationNavigatorProps {
  selectedPath: string[];
  selectedSubLocationId: number | null;
  subLocationPaths: string[][];
  onNavigate: (path: string[]) => void;
  onCreateClick: () => void;
  onManagePlansClick: () => void;
  onDeleteClick?: () => void;
  canManage: boolean;
  canDeleteSelected?: boolean;
  deleteDisabledHint?: string | null;
  deleting?: boolean;
  /** Objektanzahl je Pfad-Key (JSON.stringify der Segmente). */
  assetCountByPathKey?: Record<string, number>;
  /** Anzahl Pläne am ausgewählten Unterort (für Hinweis). */
  planCount?: number;
  /** Kompakte Darstellung (Mobile): Auswahl als Dropdown statt Baum. */
  compact?: boolean;
}

function flattenTree(node: TreeNode, out: { path: string[]; label: string; depth: number }[] = []) {
  for (const child of Array.from(node.children.values()).sort((a, b) => a.label.localeCompare(b.label, "de"))) {
    out.push({ path: child.path, label: child.label, depth: child.path.length });
    flattenTree(child, out);
  }
  return out;
}

export function SubLocationNavigator({
  selectedPath,
  selectedSubLocationId,
  subLocationPaths,
  onNavigate,
  onCreateClick,
  onManagePlansClick,
  onDeleteClick,
  canManage,
  canDeleteSelected = false,
  deleteDisabledHint = null,
  deleting = false,
  assetCountByPathKey = {},
  planCount,
  compact = false,
}: SubLocationNavigatorProps) {
  const tree = useMemo(() => buildLocationTree(subLocationPaths), [subLocationPaths]);
  const [query, setQuery] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set([""]));

  const selectedLabel = selectedPath.length > 0 ? selectedPath[selectedPath.length - 1] : "Alle Bereiche";
  const totalCount = useMemo(() => sumCountsUnder(tree, assetCountByPathKey), [tree, assetCountByPathKey]);
  const flat = useMemo(() => flattenTree(tree), [tree]);
  const hasAreas = flat.length > 0;

  // Auswahlpfad immer aufklappen
  useEffect(() => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.add("");
      for (let i = 0; i < selectedPath.length; i += 1) {
        next.add(pathKeyFromSegments(selectedPath.slice(0, i + 1)));
      }
      return next;
    });
  }, [selectedPath]);

  const normalizedQuery = query.trim().toLowerCase();

  const matchingKeys = useMemo(() => {
    if (!normalizedQuery) return null;
    const keys = new Set<string>();
    function walk(node: TreeNode) {
      const key = pathKeyFromSegments(node.path);
      const label = node.path.length ? node.label : "Alle Bereiche";
      const selfMatch = label.toLowerCase().includes(normalizedQuery);
      let childMatch = false;
      for (const child of node.children.values()) {
        if (walk(child)) childMatch = true;
      }
      if (selfMatch || childMatch) {
        keys.add(key);
        for (let i = 0; i < node.path.length; i += 1) {
          keys.add(pathKeyFromSegments(node.path.slice(0, i)));
        }
        return true;
      }
      return false;
    }
    walk(tree);
    return keys;
  }, [normalizedQuery, tree]);

  useEffect(() => {
    if (!matchingKeys) return;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      for (const key of matchingKeys) next.add(key);
      return next;
    });
  }, [matchingKeys]);

  function toggleExpanded(pathKey: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  }

  const rootChildren = useMemo(
    () => Array.from(tree.children.values()).sort((a, b) => a.label.localeCompare(b.label, "de")),
    [tree],
  );

  const actions = canManage ? (
    <div className="huelle-nav-actions">
      <button type="button" className="huelle-nav-action" onClick={onCreateClick} title="Neuen Unterort (Bereich) anlegen">
        <FolderPlusIcon className="h-4 w-4" aria-hidden />
        <span>Unterort</span>
      </button>
      <button
        type="button"
        className="huelle-nav-action"
        disabled={!selectedSubLocationId}
        onClick={onManagePlansClick}
        title={
          selectedSubLocationId
            ? "Pläne des ausgewählten Unterorts verwalten"
            : "Bitte zuerst einen Unterort auswählen"
        }
      >
        <MapIcon className="h-4 w-4" aria-hidden />
        <span>Pläne{typeof planCount === "number" && selectedSubLocationId ? ` (${planCount})` : ""}</span>
      </button>
      {onDeleteClick ? (
        <button
          type="button"
          className="huelle-nav-action huelle-nav-action--danger"
          disabled={!selectedSubLocationId || !canDeleteSelected || deleting}
          title={
            !selectedSubLocationId
              ? "Bitte zuerst einen Unterort auswählen"
              : deleteDisabledHint || "Ausgewählten Unterort löschen"
          }
          onClick={onDeleteClick}
        >
          <TrashIcon className="h-4 w-4" aria-hidden />
          <span>{deleting ? "Lösche …" : "Löschen"}</span>
        </button>
      ) : null}
    </div>
  ) : null;

  if (compact) {
    return (
      <section className="huelle-nav-panel huelle-nav-panel--compact" aria-label="Bereich wählen">
        <div className="flex items-center gap-2">
          <label className="min-w-0 flex-1">
            <span className="sr-only">Bereich</span>
            <NativeSelect
              selectSize="sm"
              value={pathKeyFromSegments(selectedPath)}
              onChange={(e) => {
                const key = e.target.value;
                const match = flat.find((entry) => pathKeyFromSegments(entry.path) === key);
                onNavigate(match ? match.path : []);
              }}
            >
              <option value={pathKeyFromSegments([])}>Alle Bereiche ({totalCount})</option>
              {flat.map((entry) => {
                const key = pathKeyFromSegments(entry.path);
                const count = Number(assetCountByPathKey[key] || 0);
                return (
                  <option key={key} value={key}>
                    {"\u00a0".repeat(Math.max(0, entry.depth - 1) * 2)}
                    {entry.label}
                    {count ? ` (${count})` : ""}
                  </option>
                );
              })}
            </NativeSelect>
          </label>
        </div>
        {actions}
      </section>
    );
  }

  return (
    <section className="huelle-nav-panel" aria-label="Bereiche und Unterorte">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-label">Bereiche</h3>
          <p className="mt-0.5 truncate text-xs text-label-tertiary" title={selectedPath.join(" › ") || "Alle Bereiche"}>
            {selectedLabel}
          </p>
        </div>
        {selectedPath.length > 0 ? (
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-accent hover:underline"
            onClick={() => onNavigate([])}
          >
            Alle anzeigen
          </button>
        ) : null}
      </div>

      {flat.length > 6 ? (
        <div className="huelle-toolbar__search mb-2 !max-w-none">
          <MagnifyingGlassIcon className="huelle-toolbar__search-icon" aria-hidden />
          <input
            className="eh-input huelle-toolbar__search-input"
            placeholder="Bereich suchen …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      ) : null}

      <div className="huelle-area-tree" role="tree" aria-label="Bereichsübersicht">
        <AreaTreeRow
          label="Alle Bereiche"
          path={[]}
          depth={0}
          selectedPath={selectedPath}
          expanded={expandedKeys.has("")}
          hasChildren={rootChildren.length > 0}
          assetCount={totalCount}
          queryActive={Boolean(normalizedQuery)}
          onToggleExpand={() => toggleExpanded("")}
          onSelect={() => onNavigate([])}
        />
        {rootChildren.length ? (
          expandedKeys.has("") ? (
            rootChildren.map((child) => (
              <AreaTreeBranch
                key={pathKeyFromSegments(child.path)}
                node={child}
                depth={1}
                selectedPath={selectedPath}
                expandedKeys={expandedKeys}
                matchingKeys={matchingKeys}
                assetCountByPathKey={assetCountByPathKey}
                onToggleExpand={toggleExpanded}
                onNavigate={onNavigate}
              />
            ))
          ) : null
        ) : null}
        {normalizedQuery &&
        matchingKeys &&
        !rootChildren.some((child) => matchingKeys.has(pathKeyFromSegments(child.path))) ? (
          <p className="px-2 py-2 text-sm text-label-tertiary">Kein Bereich passt zur Suche.</p>
        ) : null}
      </div>

      {!hasAreas ? (
        <p className="mt-2 text-xs leading-relaxed text-label-tertiary">
          Unterorte gliedern große Standorte (z. B. Gebäude, Etage, Bereich). Sie sind optional – Objekte können
          auch direkt am Standort liegen.
        </p>
      ) : null}

      {actions}
    </section>
  );
}

function AreaTreeBranch({
  node,
  depth,
  selectedPath,
  expandedKeys,
  matchingKeys,
  assetCountByPathKey,
  onToggleExpand,
  onNavigate,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string[];
  expandedKeys: Set<string>;
  matchingKeys: Set<string> | null;
  assetCountByPathKey: Record<string, number>;
  onToggleExpand: (key: string) => void;
  onNavigate: (path: string[]) => void;
}) {
  const pathKey = pathKeyFromSegments(node.path);
  if (matchingKeys && !matchingKeys.has(pathKey)) return null;

  const children = Array.from(node.children.values()).sort((a, b) => a.label.localeCompare(b.label, "de"));
  const expanded = expandedKeys.has(pathKey);
  const count = Number(assetCountByPathKey[pathKey] || 0);

  return (
    <>
      <AreaTreeRow
        label={node.label}
        path={node.path}
        depth={depth}
        selectedPath={selectedPath}
        expanded={expanded}
        hasChildren={children.length > 0}
        assetCount={count}
        queryActive={Boolean(matchingKeys)}
        onToggleExpand={() => onToggleExpand(pathKey)}
        onSelect={() => onNavigate(node.path)}
      />
      {expanded
        ? children.map((child) => (
            <AreaTreeBranch
              key={pathKeyFromSegments(child.path)}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              expandedKeys={expandedKeys}
              matchingKeys={matchingKeys}
              assetCountByPathKey={assetCountByPathKey}
              onToggleExpand={onToggleExpand}
              onNavigate={onNavigate}
            />
          ))
        : null}
    </>
  );
}

function AreaTreeRow({
  label,
  path,
  depth,
  selectedPath,
  expanded,
  hasChildren,
  assetCount,
  queryActive,
  onToggleExpand,
  onSelect,
}: {
  label: string;
  path: string[];
  depth: number;
  selectedPath: string[];
  expanded: boolean;
  hasChildren: boolean;
  assetCount: number;
  queryActive: boolean;
  onToggleExpand: () => void;
  onSelect: () => void;
}) {
  const isSelected = JSON.stringify(path) === JSON.stringify(selectedPath);
  const isAncestor =
    !isSelected &&
    path.length > 0 &&
    selectedPath.length > path.length &&
    path.every((segment, index) => selectedPath[index] === segment);

  return (
    <div
      className={clsx("huelle-area-tree__row", isSelected && "is-selected", isAncestor && "is-ancestor")}
      style={{ paddingLeft: `${0.2 + depth * 0.9}rem` }}
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={hasChildren ? expanded : undefined}
    >
      {hasChildren ? (
        <button
          type="button"
          className="huelle-area-tree__chevron"
          aria-label={expanded ? "Zuklappen" : "Aufklappen"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
        >
          {expanded ? <ChevronDownIcon className="h-4 w-4" aria-hidden /> : <ChevronRightIcon className="h-4 w-4" aria-hidden />}
        </button>
      ) : (
        <span className="huelle-area-tree__chevron-spacer" aria-hidden />
      )}
      <button type="button" className="huelle-area-tree__label" onClick={onSelect} title={label}>
        <span className="truncate">{label}</span>
        {assetCount > 0 || queryActive ? <span className="huelle-area-tree__count">{assetCount}</span> : null}
      </button>
    </div>
  );
}

function sumCountsUnder(node: TreeNode, counts: Record<string, number>): number {
  let total = Number(counts[pathKeyFromSegments(node.path)] || 0);
  for (const child of node.children.values()) {
    total += sumCountsUnder(child, counts);
  }
  return total;
}
